import Component from '@glimmer/component'
import { cached } from '@glimmer/tracking'
import { service } from '@ember/service'
import { action } from '@ember/object'
import { assert } from '@ember/debug'
import { optionTemplate, optionTemplateSubLevel } from 'teacher-ui-client/utils/dropdown-templates'
import { t } from 'ember-intl'
import type { IntlService } from 'ember-intl'
import type Store from '@ember-data/store'
import type RouterService from '@ember/routing/router-service'
import type SessionService from 'teacher-ui-client/services/session'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import type { UIScope } from 'teacher-ui-client/reporting/ui-scope/route'
import FormComponentsFormSelect from 'teacher-ui-client/components/form-components/form-select'
import type SchoolClass from 'teacher-ui-client/models/school-class'
import type Teacher from 'teacher-ui-client/models/teacher'
import { validUiScopesFor } from 'teacher-ui-client/reporting/ui-scope/route'
import type { SelectOption } from 'teacher-ui-client/components/form-components/form-select/option'
import type DateFilterService from 'teacher-ui-client/services/date-filter'
import { hasId } from 'teacher-ui-client/utils/type-guards'

type UIScopeOption = Record<PropertyKey, unknown> & UIScope & SelectOption

type UIScopeTeacherOption = {
  studentCount: number
  value: UIScopeSchoolClassOption[]
  [key: PropertyKey]: unknown
} & UIScopeOption

type UIScopeSchoolClassOption = {
  studentCount: number
  [key: PropertyKey]: unknown
} & UIScopeOption

interface Signature {
  Args: {
    disabled?: boolean
  }
  Element: HTMLElement
}

/**
 * This component presents a set of scope options to the user to pick. These are generated from an array of valid ui
 * scopes that the user is allowed to visit.
 * It determines the currently selected UiScope option from the URL, and when the user chooses an option, it applies it
 * to the URL.
 *
 * Intended to be used only on reporting pages, specifically pages that have access to the ui scope dynamic segments.
 */
export class ReportingUiScopeSelect extends Component<Signature> {
  @service intl!: IntlService

  @service router!: RouterService

  @service session!: SessionService

  @service subscriptionType!: SubscriptionTypeService

  @service store!: Store

  @service dateFilter!: DateFilterService

  selectedTemplate = '${selectedText}'

  optionTemplate = optionTemplate

  optionTemplateSubLevel = optionTemplateSubLevel

  get isSubco() {
    return this.subscriptionType.current && this.session.currentTeacher.subcoFor(this.subscriptionType.current)
  }

  /**
   * The uiScope indicated by the URL, or undefined if not present.
   */
  get uiScope() {
    const uiScopeRouteInfo = this.router.currentRoute?.find((routeInfo) => {
      if (!routeInfo.params) return false
      const paramKeys = Object.keys(routeInfo.params)
      return paramKeys.includes('ui_scope_name') && paramKeys.includes('ui_scope_id')
    })
    if (uiScopeRouteInfo?.params) {
      const { ui_scope_name: scope, ui_scope_id: id } = uiScopeRouteInfo.params
      return { scope, id }
    }
    return undefined
  }

  /**
   * Determines the selected scope option based on the ui-scope present in the URL dynamic segments.
   *
   * Note that the school-class options are repeated (one per teacher) - if the scope is school-class, this getter just
   * finds the first school-class option that matches the ui-scope id. This means that the option that was clicked may
   * not necessarily be exactly the one that gets displayed. This is apparent when those options have different
   * "selectedText" (eg teacher names are different), and is a limitation of the current architecture, as which option
   * was specifically chosen is not stored in the URL (only the class id).
   *
   * Storing some state about the selected school-class option in some tracked property on this component may work to
   * some degree, but would be lost on a page refresh anyway.
   *
   * A possible remedy for this would be to display _all_ the teacher names in some way for that selected class (that
   * way, the options don't differ).
   */
  get selectedOption() {
    const { uiScope } = this
    const isSelectedOption = (option: UIScopeOption) => option.scope === uiScope?.scope && option.id === uiScope?.id

    const allOptions = this.uiScopeOptions.all
    for (const schoolOrTeacherOption of allOptions) {
      if (isSelectedOption(schoolOrTeacherOption)) return schoolOrTeacherOption
      if (typeof schoolOrTeacherOption.value !== 'string') {
        const schoolClassOptions = (schoolOrTeacherOption as UIScopeTeacherOption).value
        for (const schoolClassOption of schoolClassOptions) {
          if (isSelectedOption(schoolClassOption)) return schoolClassOption
        }
      }
    }
    return undefined
  }

  /**
   * Generates a flat array of ui scopes that this user can visit
   */
  @cached
  get validUiScopes(): UIScope[] {
    const subscriptionType = this.subscriptionType.current
    assert('subscriptionType required for ui-scope-select', typeof subscriptionType !== 'undefined')
    return validUiScopesFor(this.session.currentSchool, this.session.currentTeacher, subscriptionType)
  }

  /**
   * Options are an array of grouped option objects which represent ui scopes, that can be used with the "FormSelect"
   * component.
   */
  @cached
  get uiScopeOptions() {
    // Before options remain in the list at the top regardless of search query
    let beforeOptions: UIScopeOption[] = []
    const schoolId = this.session.currentSchool.id
    const hasSchoolScope = this.validUiScopes.some(({ scope, id }) => scope === 'school' && id === schoolId)
    if (hasSchoolScope) {
      const wholeSchoolText = this.intl.t('reporting.reporting.classPicker.schoolAll')
      const schoolOption: UIScopeOption = {
        uid: `school_${schoolId}`, // ensure all uid's are unique
        id: schoolId,
        scope: 'school',
        value: schoolId,
        text: wholeSchoolText,
        selectedText: wholeSchoolText,
      }
      beforeOptions = [schoolOption]
    }

    // Generate grouped options for teachers & their classes. Only use records that represent a valid scope
    const isValidTeacher = (teacher: Teacher) =>
      this.validUiScopes.some(({ scope, id }) => scope === 'teacher' && id === teacher.id)
    const isValidSchoolClass = (schoolClass: SchoolClass) =>
      this.validUiScopes.some(({ scope, id }) => scope === 'school-class' && id === schoolClass.id)
    const validTeachers = this.store.peekAll<Teacher>('teacher').filter(isValidTeacher).filter(hasId) ?? []

    const standardOptions: UIScopeTeacherOption[] = validTeachers
      .map((teacher) => {
        // Create a teacher option
        const teacherId = teacher.id
        const schoolClassOptions: UIScopeSchoolClassOption[] = teacher.schoolClasses
          .filter(isValidSchoolClass)
          .filter(hasId)
          .map((schoolClass) => {
            // Create a school class option
            const schoolClassId = schoolClass.id
            return {
              uid: `teacher_${teacherId}_school-class_${schoolClassId}`, // ensure each class option uid is unique
              id: schoolClassId,
              text: schoolClass.name,
              selectedText: `${teacher.fullName} - ${schoolClass.name}`,
              value: schoolClassId,
              scope: 'school-class' as const,
              studentCount: schoolClass.students.length,
              studentIds: schoolClass.studentIds,
            }
          })
        const uniqueStudentIds = new Set(schoolClassOptions.flatMap((option) => option.studentIds))
        return {
          uid: `teacher_${teacherId}`,
          id: teacherId,
          scope: 'teacher' as const,
          value: schoolClassOptions,
          text: teacher.fullName,
          selectedText: teacher.fullName,
          studentCount: uniqueStudentIds.size,
        }
      })
      .sort((a, b) => a.text.localeCompare(b.text))

    return { all: [...beforeOptions, ...standardOptions], before: beforeOptions, standard: standardOptions }
  }

  @action
  changeScopeOption(scopeOption: UIScopeOption) {
    const uiScopeName = scopeOption.scope
    const uiScopeId = scopeOption.id
    // ensure date range stays the same when changing scope. reset others, to avoid them being tied to a scope
    void this.router.transitionTo(this.router.currentRouteName, uiScopeName, uiScopeId, {
      queryParams: { dateRange: this.dateFilter.dateRange, selectedLesson: 1, contentLevel: 'all' },
    })
  }

  // When reset, it should select the initial "unscoped" option
  @action
  resetScopeOption() {
    const { uiScopeOptions } = this
    this.changeScopeOption(uiScopeOptions.before[0] ?? uiScopeOptions.standard[0])
  }

  // eslint-disable no-undef
  <template>
    <FormComponentsFormSelect
      data-test-ui-scope-select
      class="form-components-form-select-ui-scope-select"
      @defaultText={{t "reporting.dropdownSelectSchool"}}
      @staticBeforeOptions={{this.uiScopeOptions.before}}
      @options={{this.uiScopeOptions.standard}}
      @search={{true}}
      @searchPlaceholder={{if
        this.isSubco
        (t "reporting.dropdownSearchTeachers")
        (t "reporting.dropdownSearchClasses")
      }}
      @optionClick={{this.changeScopeOption}}
      @resetAction={{this.resetScopeOption}}
      @value={{this.selectedOption}}
      @optionTemplate={{this.optionTemplate}}
      @optionTemplateSubLevel={{this.optionTemplateSubLevel}}
      @selectedTemplate={{this.selectedTemplate}}
      @selectedTemplateSubLevel={{this.selectedTemplate}}
      @disabled={{@disabled}}
      ...attributes
    />
  </template>
}

export default ReportingUiScopeSelect
